import { useCallback, useEffect, useState, type FormEvent } from 'react'
import {
  Alert,
  Modal,
  StatusBadge,
  card,
  colors,
  input,
  label as labelStyle,
  btnPrimary,
  btnSecondary,
} from '../../components/ui'
import { api, ApiError } from '../../services/api'

const TENANCY_STATUSES = ['active', 'suspended', 'expired', 'revoked'] as const
type TenancyStatus = (typeof TENANCY_STATUSES)[number]

// One row of GET /api/fleet/tenancies (the list projection in
// repositories/tenancies.ts). The Tenancy is the license source-of-truth, so
// the license columns live here, keyed to an organization on this environment.
interface TenancyRow {
  readonly id: string
  readonly organizationId: string
  readonly organizationName: string | null
  readonly environmentId: string | null
  readonly environmentName: string | null
  readonly name: string
  readonly licenseStatus: TenancyStatus
  readonly licenseTier: string
  readonly licenseExpiresAt: string | null
  readonly licenseFeatures: readonly string[]
  readonly activatedAt: string | null
  readonly createdAt: string
}

interface CustomerOption {
  readonly id: string
  readonly name: string
}

interface MintResponse {
  readonly id: string
  readonly name: string
  readonly plaintextKey: string
  readonly displayedOnce: true
}

interface RotateResponse {
  readonly plaintextKey: string
  readonly displayedOnce: true
}

// Turn an ISO timestamp into the YYYY-MM-DD a <input type="date"> expects.
function isoToDateInput(iso: string | null): string {
  if (!iso) return ''
  return iso.slice(0, 10)
}

// A YYYY-MM-DD date input back to the ISO datetime the API's Zod schema wants
// (z.string().datetime()), or null when cleared.
function dateInputToIso(value: string): string | null {
  return value ? new Date(value + 'T00:00:00Z').toISOString() : null
}

function csvToList(csv: string): string[] {
  return csv
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

function statusBadge(status: TenancyStatus): 'active' | 'warning' | 'inactive' {
  if (status === 'active') return 'active'
  if (status === 'suspended' || status === 'expired') return 'warning'
  return 'inactive'
}

// medflow_admin-only. The per-Tenancy license editor that lives on the
// environment detail page. Lists the tenancies bound to this Environment
// (one Organization's licensed presence each), and supports minting a new
// tenancy (license key shown once), editing its license
// (status/tier/expiry/features/name), and rotating its key. Replaces the old
// env-level license card + per-org overrides now that licensing is per-tenancy.
export function TenanciesCard({
  environmentId,
  defaultOrganizationId,
}: {
  readonly environmentId: string
  readonly defaultOrganizationId?: string | null
}) {
  const [tenancies, setTenancies] = useState<readonly TenancyRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Plaintext key shown exactly once after a mint or rotate.
  const [mintedKey, setMintedKey] = useState<{ readonly name: string; readonly key: string } | null>(null)

  const loadTenancies = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get<readonly TenancyRow[]>(`/fleet/tenancies?environmentId=${environmentId}`)
      setTenancies(res.data ?? [])
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load tenancies')
    } finally {
      setLoading(false)
    }
  }, [environmentId])

  useEffect(() => {
    loadTenancies()
  }, [loadTenancies])

  // ── create ──
  const [createOpen, setCreateOpen] = useState(false)
  const [customers, setCustomers] = useState<readonly CustomerOption[]>([])
  const [orgId, setOrgId] = useState('')
  const [newName, setNewName] = useState('')
  const [newTier, setNewTier] = useState('standard')
  const [newExpires, setNewExpires] = useState('')
  const [newFeatures, setNewFeatures] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const openCreate = useCallback(() => {
    setCreateError(null)
    setOrgId(defaultOrganizationId ?? '')
    setNewName('')
    setNewTier('standard')
    setNewExpires('')
    setNewFeatures('')
    setCreateOpen(true)
    // Lazy-load the customer list the first time the form opens.
    if (customers.length === 0) {
      api
        .get<readonly CustomerOption[]>('/fleet/customers')
        .then((res) => setCustomers(res.data ?? []))
        .catch(() => {
          /* non-fatal — the org <select> just stays empty */
        })
    }
  }, [customers.length, defaultOrganizationId])

  async function submitCreate(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (!orgId) {
      setCreateError('Select a customer organization')
      return
    }
    setCreating(true)
    setCreateError(null)
    try {
      const res = await api.post<MintResponse>('/fleet/tenancies', {
        organizationId: orgId,
        environmentId,
        name: newName.trim(),
        tier: newTier.trim() || undefined,
        features: csvToList(newFeatures),
        expiresAt: dateInputToIso(newExpires),
      })
      if (res.data) setMintedKey({ name: res.data.name, key: res.data.plaintextKey })
      setCreateOpen(false)
      await loadTenancies()
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Failed to create tenancy')
    } finally {
      setCreating(false)
    }
  }

  // ── edit ──
  const [editing, setEditing] = useState<TenancyRow | null>(null)
  const [editName, setEditName] = useState('')
  const [editStatus, setEditStatus] = useState<TenancyStatus>('active')
  const [editTier, setEditTier] = useState('')
  const [editExpires, setEditExpires] = useState('')
  const [editFeatures, setEditFeatures] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  function openEdit(row: TenancyRow): void {
    setEditing(row)
    setEditName(row.name)
    setEditStatus(row.licenseStatus)
    setEditTier(row.licenseTier)
    setEditExpires(isoToDateInput(row.licenseExpiresAt))
    setEditFeatures(row.licenseFeatures.join(', '))
    setEditError(null)
  }

  async function submitEdit(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (!editing) return
    setSavingEdit(true)
    setEditError(null)
    try {
      await api.patch(`/fleet/tenancies/${editing.id}`, {
        name: editName.trim(),
        status: editStatus,
        tier: editTier.trim(),
        features: csvToList(editFeatures),
        expiresAt: dateInputToIso(editExpires),
      })
      setEditing(null)
      await loadTenancies()
    } catch (err) {
      setEditError(err instanceof ApiError ? err.message : 'Failed to save tenancy')
    } finally {
      setSavingEdit(false)
    }
  }

  // ── rotate ──
  const [rotatingId, setRotatingId] = useState<string | null>(null)

  async function rotateKey(row: TenancyRow): Promise<void> {
    if (!confirm(`Rotate the license key for "${row.name}"? The old key stops working immediately.`)) return
    setRotatingId(row.id)
    setError(null)
    try {
      const res = await api.post<RotateResponse>(`/fleet/tenancies/${row.id}/rotate-key`, {})
      if (res.data) setMintedKey({ name: row.name, key: res.data.plaintextKey })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Rotate failed')
    } finally {
      setRotatingId(null)
    }
  }

  const cellStyle = { padding: '6px 8px', borderBottom: `1px solid ${colors.border}` } as const

  return (
    <div style={{ ...card, maxWidth: '900px', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Tenancies</h3>
        <button type="button" onClick={openCreate} style={btnPrimary} data-testid="add-tenancy">
          Add tenancy
        </button>
      </div>
      <p style={{ marginTop: '0.25rem', fontSize: '13px', color: colors.textMuted }}>
        Each tenancy is one organization&apos;s licensed presence on this environment. The tenancy holds the license
        (status, tier, expiry, features); the environment itself is unlicensed.
      </p>

      {error && <Alert type="error" message={error} onDismiss={() => setError(null)} />}

      {mintedKey && (
        <div
          style={{
            background: '#fef3c7',
            border: '1px solid #fcd34d',
            color: '#92400e',
            padding: '1rem',
            borderRadius: '4px',
            margin: '0.5rem 0 1rem',
          }}
          data-testid="tenancy-minted-key"
        >
          <div style={{ marginBottom: '0.5rem' }}>
            <strong>License key for {mintedKey.name}.</strong> Shown exactly once — copy it into the organization&apos;s
            activation step. Lost it? Rotate the key.
          </div>
          <code style={{ display: 'block', wordBreak: 'break-all', padding: '0.5rem', background: '#fff' }}>
            {mintedKey.key}
          </code>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <button type="button" onClick={() => setMintedKey(null)} style={btnSecondary}>
              Done
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Loading...</p>
      ) : tenancies.length === 0 ? (
        <p style={{ color: colors.textMuted, fontSize: '0.875rem' }}>
          No tenancies on this environment yet. Add one to license an organization here.
        </p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr>
              {['Organization', 'Tenancy', 'Status', 'Tier', 'Expires', 'Features', ''].map((h) => (
                <th key={h} style={{ ...cellStyle, textAlign: 'left', color: colors.textMuted, fontSize: '0.75rem' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tenancies.map((t) => (
              <tr key={t.id} data-testid={`tenancy-row-${t.id}`}>
                <td style={cellStyle}>{t.organizationName ?? '—'}</td>
                <td style={cellStyle}>{t.name}</td>
                <td style={cellStyle}>
                  <StatusBadge status={statusBadge(t.licenseStatus)} label={t.licenseStatus} />
                </td>
                <td style={cellStyle}>{t.licenseTier}</td>
                <td style={cellStyle}>
                  {t.licenseExpiresAt ? new Date(t.licenseExpiresAt).toLocaleDateString() : '—'}
                </td>
                <td style={{ ...cellStyle, color: colors.textMuted }}>
                  {t.licenseFeatures.length > 0 ? t.licenseFeatures.join(', ') : '—'}
                </td>
                <td style={cellStyle}>
                  <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => openEdit(t)}
                      style={{ ...btnSecondary, fontSize: '12px', padding: '0.2rem 0.5rem' }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => rotateKey(t)}
                      disabled={rotatingId === t.id}
                      style={{ ...btnSecondary, fontSize: '12px', padding: '0.2rem 0.5rem' }}
                    >
                      {rotatingId === t.id ? 'Rotating...' : 'Rotate key'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Create tenancy modal */}
      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title="Add tenancy" width="520px">
        <form onSubmit={submitCreate}>
          {createError && <Alert type="error" message={createError} onDismiss={() => setCreateError(null)} />}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Customer organization</label>
            <select
              required
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              style={input}
              data-testid="tenancy-org"
            >
              <option value="">-- Select customer --</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Tenancy name</label>
            <input
              type="text"
              required
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Acme — West clinic"
              style={input}
              data-testid="tenancy-name"
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Tier</label>
              <input type="text" value={newTier} onChange={(e) => setNewTier(e.target.value)} style={input} />
            </div>
            <div>
              <label style={labelStyle}>Expires</label>
              <input type="date" value={newExpires} onChange={(e) => setNewExpires(e.target.value)} style={input} />
            </div>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <label style={labelStyle}>Feature flags (comma-separated)</label>
            <input
              type="text"
              value={newFeatures}
              onChange={(e) => setNewFeatures(e.target.value)}
              placeholder="remote_access, hipaa"
              style={input}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
            <button type="button" onClick={() => setCreateOpen(false)} style={btnSecondary}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating || !newName.trim()}
              style={btnPrimary}
              data-testid="submit-tenancy"
            >
              {creating ? 'Creating...' : 'Create & mint key'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit tenancy modal */}
      <Modal
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        title={editing ? `Edit ${editing.name}` : 'Edit tenancy'}
        width="520px"
      >
        <form onSubmit={submitEdit}>
          {editError && <Alert type="error" message={editError} onDismiss={() => setEditError(null)} />}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Tenancy name</label>
            <input type="text" required value={editName} onChange={(e) => setEditName(e.target.value)} style={input} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Status</label>
              <select
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value as TenancyStatus)}
                style={input}
                data-testid="tenancy-status"
              >
                {TENANCY_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Tier</label>
              <input type="text" value={editTier} onChange={(e) => setEditTier(e.target.value)} style={input} />
            </div>
            <div>
              <label style={labelStyle}>Expires</label>
              <input type="date" value={editExpires} onChange={(e) => setEditExpires(e.target.value)} style={input} />
            </div>
            <div>
              <label style={labelStyle}>Feature flags (comma-separated)</label>
              <input
                type="text"
                value={editFeatures}
                onChange={(e) => setEditFeatures(e.target.value)}
                placeholder="remote_access, hipaa"
                style={input}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
            <button type="button" onClick={() => setEditing(null)} style={btnSecondary}>
              Cancel
            </button>
            <button type="submit" disabled={savingEdit || !editName.trim() || !editTier.trim()} style={btnPrimary}>
              {savingEdit ? 'Saving...' : 'Save tenancy'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
